import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { AvailabilityForm } from '@/components/shared/availability/AvailabilityForm';
import { TSCard } from '@/components/shared/card/Card';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { merge } from '@/helpers/Utility';
import { useSaveMySchedule } from '@/services/availability/useSaveMySchedule';
import { EmployeeSchedule } from '@/services/employee/schedule/schedule.types';
import { useMySchedule } from '@/services/employee/schedule/useMySchedule';
import { FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MyAvailabilityHeader } from './MyAvailabilityHeader';

interface FormValues {
  availabilityType: string;
  startTime: string;
  endTime: string;
}

const MyAvailability: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const dateObj = useMemo(() => new Date(date), [date]);

  const [selectedDate, setSelectedDate] = useState<Date>(dateObj);

  useEffect(() => {
    setSelectedDate(dateObj);
  }, [dateObj]);

  const data = useMySchedule(selectedDate);
  const { save, fetching } = useSaveMySchedule(selectedDate, selectedDate);

  const onSave = async (payload: EmployeeSchedule) => {
    await save(payload);
    navigate('/ess/my-calendar/');
  };

  const onChangeDate = (newDate: Date) => {
    setSelectedDate(newDate);
    navigate(`/ess/my-calendar/${dateToApiFormat(newDate)}/my-availability`, { replace: true });
  };

  return (
    <PageContainer customHeader={<MyAvailabilityHeader />}>
      <div className="bg-white">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={selectedDate} onChange={onChangeDate} />
        </PageSizer>
      </div>
      <Suspense fallback={<TableLoader />}>
        <div className="overflow-y-hidden flex flex-col bg-white">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            <SubmissionErrorBoundary>
              <TSCard className={merge('p-4 border-0 lg:border')}>
                <AvailabilityForm
                  key={dateToApiFormat(selectedDate)}
                  date={selectedDate}
                  existingDay={data.schedule[dateToApiFormat(selectedDate)]}
                  fetching={fetching}
                  onSave={onSave}
                  onCancel={() => navigate(-1)}
                />
              </TSCard>
            </SubmissionErrorBoundary>
          </PageSizer>
        </div>
      </Suspense>
    </PageContainer>
  );
};

export default MyAvailability;
